import React from 'react';
import { Document, View, Text, StyleSheet } from '@react-pdf/renderer';
import { AttendanceStatus, InformationType } from '../../../types';
import {
  Page,
  pdfColors, pdfLetter, pdfSpacing,
  type SchoolBranding, type OfficialDetail,
} from '../../../utils/pdf';
import { STATUS_VISUAL, INFO_INDICATOR_STATUSES, NON_WORKING_STATUSES, INFO_DOT } from '../../../utils/attendanceStatus';

/** One person's month: the cells before the day grid (roll and name, or name and role), and a mark per date. */
export interface RegisterRow {
  id: string;
  cells: string[];
  attendance: Record<string, { status: AttendanceStatus; informationType?: InformationType } | undefined>;
}

/** A column before the day grid — fixed width, or taking what's left when `width` is absent. */
export interface RegisterLeadColumn {
  header: string;
  width?: number;
  /** The person's name — set in the darker weight. */
  strong?: boolean;
}

interface Props {
  /** "Student Attendance Report", "Employee Attendance Report". */
  title: string;
  /** Under the letterhead — who and which month. */
  details: OfficialDetail[];
  /** The running header on later pages. */
  subtitle: string;
  lead: RegisterLeadColumn[];
  rows: RegisterRow[];
  dates: string[];
  /** Count approved leave as its own total (employees). */
  withLeaves?: boolean;
  branding: SchoolBranding;
  generatedAt: string;
}

// A4 landscape inside the page frame: 762pt across. Days shrink to fit, never below a badge's width.
const CONTENT_W = 762;
const TOTAL_W = 31;
const MIN_FLEX_W = 96;
const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/** The totals each person carries, in the server register's order. */
const TOTALS = [
  { key: 'working', header: 'Working', color: '#0284c7' },
  { key: 'present', header: 'Present', color: '#059669' },
  { key: 'late', header: 'Late', color: '#d97706' },
  { key: 'absent', header: 'Absent', color: '#dc2626' },
  { key: 'leaves', header: 'Leaves', color: '#0891b2' },
] as const;
type TotalKey = typeof TOTALS[number]['key'];

const HAIR = { borderBottomWidth: 0.5, borderBottomColor: pdfColors.divider, borderBottomStyle: 'solid' } as const;

const styles = StyleSheet.create({
  head: { flexDirection: 'row', alignItems: 'center', backgroundColor: pdfColors.textPrimary, borderRadius: 3 },
  th: { color: pdfColors.textOnDark, fontSize: 5.8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: pdfLetter.openLabel, paddingVertical: 5, paddingHorizontal: 4 },
  dayHead: { alignItems: 'center', paddingVertical: 3 },
  dow: { color: pdfColors.textOnDark, fontSize: 5, opacity: 0.7 },
  dnum: { color: pdfColors.textOnDark, fontSize: 6.8, fontWeight: 700, marginTop: 0.5 },
  row: { flexDirection: 'row', alignItems: 'center', minHeight: 17, ...HAIR },
  zebra: { backgroundColor: pdfColors.surfaceSoft },
  cell: { paddingHorizontal: 4, paddingVertical: 3, fontSize: 7, color: pdfColors.textMuted },
  strong: { fontSize: 7.5, fontWeight: 600, color: pdfColors.textPrimary },
  day: { alignItems: 'center', justifyContent: 'center' },
  badge: { minWidth: 12, height: 11, paddingHorizontal: 1.5, borderRadius: 2.5, alignItems: 'center', justifyContent: 'center' },
  badgeText: { color: '#ffffff', fontSize: 5.3, fontWeight: 700 },
  dot: { position: 'absolute', top: -1.4, right: -1.4, width: 3.6, height: 3.6, borderRadius: 1.8, borderWidth: 0.6, borderColor: '#ffffff', borderStyle: 'solid' },
  empty: { fontSize: 8, color: pdfColors.dividerStrong },
  total: { width: TOTAL_W, textAlign: 'center', fontSize: 7.5, fontWeight: 700 },
  totalFirst: { borderLeftWidth: 0.5, borderLeftColor: pdfColors.divider, borderLeftStyle: 'solid' },
  legend: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', marginTop: pdfSpacing.md },
  legendTitle: { fontSize: 6, fontWeight: 700, textTransform: 'uppercase', letterSpacing: pdfLetter.openLabel, color: pdfColors.textMuted, marginRight: 8 },
  legendItem: { flexDirection: 'row', alignItems: 'center', marginRight: 12, marginVertical: 2 },
  legendLabel: { fontSize: 7, color: pdfColors.textSecondary, marginLeft: 4 },
  legendDot: { width: 5, height: 5, borderRadius: 2.5 },
});

const dayOfWeek = (date: string) => new Date(`${date}T00:00:00Z`).getUTCDay();

const Badge: React.FC<{ status: AttendanceStatus; info?: InformationType }> = ({ status, info }) => {
  const v = STATUS_VISUAL[status];
  const dot = info && INFO_INDICATOR_STATUSES.has(status)
    ? (info === InformationType.INFORMED ? INFO_DOT.informed : INFO_DOT.uninformed)
    : null;
  return (
    <View style={[styles.badge, { backgroundColor: v.bg }]}>
      <Text style={styles.badgeText}>{v.text}</Text>
      {dot && <View style={[styles.dot, { backgroundColor: dot }]} />}
    </View>
  );
};

/** One person's month, counted as the server register counted it. */
function totalsOf(row: RegisterRow, dates: string[]): Record<TotalKey, number> {
  const t = { working: 0, present: 0, late: 0, absent: 0, leaves: 0 };
  for (const d of dates) {
    const s = row.attendance[d]?.status;
    if (!s) continue;
    if (!NON_WORKING_STATUSES.has(s)) t.working += 1;
    if (s === AttendanceStatus.PRESENT) t.present += 1;
    else if (s === AttendanceStatus.LATE) t.late += 1;
    else if (s === AttendanceStatus.ABSENT) t.absent += 1;
    else if (s === AttendanceStatus.LEAVE) t.leaves += 1;
  }
  return t;
}

/**
 * A monthly attendance register — students of a class, or the school's
 * employees: a row per person, a badge per day, each person's totals and a
 * legend of what the badges mean. Landscape; the column header repeats on
 * every page.
 */
const AttendanceRegisterDocument: React.FC<Props> = ({ title, details, subtitle, lead, rows, dates, withLeaves, branding, generatedAt }) => {
  const totals = TOTALS.filter(t => t.key !== 'leaves' || withLeaves);
  const fixed = lead.reduce((w, c) => w + (c.width ?? 0), 0) + totals.length * TOTAL_W;
  const dayW = Math.min(16, (CONTENT_W - fixed - MIN_FLEX_W) / Math.max(dates.length, 1));
  const leadStyle = (c: RegisterLeadColumn) => (c.width ? { width: c.width } : { flex: 1 });

  // The legend lists only what the register uses.
  const used = new Set<AttendanceStatus>();
  let informed = false;
  let uninformed = false;
  for (const r of rows) for (const d of dates) {
    const rec = r.attendance[d];
    if (!rec) continue;
    used.add(rec.status);
    if (INFO_INDICATOR_STATUSES.has(rec.status)) {
      if (rec.informationType === InformationType.INFORMED) informed = true;
      if (rec.informationType === InformationType.UNINFORMED) uninformed = true;
    }
  }
  const legend = (Object.keys(STATUS_VISUAL) as AttendanceStatus[]).filter(s => used.has(s));

  return (
    <Document title={`${title} — ${subtitle}`} author={branding.name}>
      <Page
        branding={branding}
        orientation="landscape"
        meta={{
          title,
          subtitle,
          generatedAt,
        }}
        official={{ details }}
      >
        <View style={styles.head} fixed>
          {lead.map(c => <Text key={c.header} style={[styles.th, leadStyle(c)]}>{c.header}</Text>)}
          {dates.map(d => (
            <View key={d} style={[styles.dayHead, { width: dayW }]}>
              <Text style={styles.dow}>{DOW[dayOfWeek(d)]}</Text>
              <Text style={styles.dnum}>{Number(d.slice(8))}</Text>
            </View>
          ))}
          {totals.map(t => (
            <Text key={t.key} style={[styles.th, { width: TOTAL_W, textAlign: 'center', letterSpacing: 0, paddingHorizontal: 0 }]}>{t.header}</Text>
          ))}
        </View>

        {rows.map((r, i) => {
          const t = totalsOf(r, dates);
          return (
            <View key={r.id} style={i % 2 ? [styles.row, styles.zebra] : styles.row} wrap={false}>
              {lead.map((c, k) => (
                <Text key={c.header} style={c.strong ? [styles.cell, styles.strong, leadStyle(c)] : [styles.cell, leadStyle(c)]}>{r.cells[k]}</Text>
              ))}
              {dates.map(d => {
                const rec = r.attendance[d];
                return (
                  <View key={d} style={[styles.day, { width: dayW }]}>
                    {rec ? <Badge status={rec.status} info={rec.informationType} /> : <Text style={styles.empty}>·</Text>}
                  </View>
                );
              })}
              {totals.map((c, k) => (
                <Text key={c.key} style={k === 0 ? [styles.total, styles.totalFirst, { color: c.color }] : [styles.total, { color: c.color }]}>{t[c.key]}</Text>
              ))}
            </View>
          );
        })}

        {legend.length > 0 && (
          <View style={styles.legend} wrap={false}>
            <Text style={styles.legendTitle}>Legend</Text>
            {legend.map(s => (
              <View key={s} style={styles.legendItem}>
                <Badge status={s} />
                <Text style={styles.legendLabel}>{STATUS_VISUAL[s].label}</Text>
              </View>
            ))}
            {informed && (
              <View style={styles.legendItem}>
                <View style={[styles.legendDot, { backgroundColor: INFO_DOT.informed }]} />
                <Text style={styles.legendLabel}>Informed</Text>
              </View>
            )}
            {uninformed && (
              <View style={styles.legendItem}>
                <View style={[styles.legendDot, { backgroundColor: INFO_DOT.uninformed }]} />
                <Text style={styles.legendLabel}>Uninformed</Text>
              </View>
            )}
          </View>
        )}
      </Page>
    </Document>
  );
};

export default AttendanceRegisterDocument;
